import type { SupplierListFilters } from './earlySignalsDbIntent'
import { extractSupplierListFilters, isHighestRiskQuestion, isWhatsNewAsk } from './earlySignalsDbIntent'

export type GroupByDimension = 'risk' | 'region' | 'category' | 'procurement'

export type RiskAnalystListSession = {
  tool: 'list_suppliers' | 'count_filtered_suppliers' | 'group_suppliers_by'
  filters: SupplierListFilters
  groupBy?: GroupByDimension[]
}

/** Tools that clear last-table refine context. */
export function shouldClearListSession(tool: string | null | undefined): boolean {
  if (!tool) return true
  return !(
    tool === 'list_suppliers' ||
    tool === 'count_filtered_suppliers' ||
    tool === 'group_suppliers_by'
  )
}

export function mergeSupplierListFilters(
  base: SupplierListFilters,
  delta: SupplierListFilters
): SupplierListFilters {
  const out: SupplierListFilters = { ...base }
  if (delta.riskBand) out.riskBand = delta.riskBand
  if (delta.singleSource) out.singleSource = true
  if (delta.categoryHint) out.categoryHint = delta.categoryHint
  if (delta.subCategoryHint) out.subCategoryHint = delta.subCategoryHint
  if (delta.nameHint) out.nameHint = delta.nameHint
  if (delta.scoreFilter) out.scoreFilter = delta.scoreFilter
  if (delta.overallScoreMin != null) {
    out.overallScoreMin = delta.overallScoreMin
    out.overallScoreExclusive = delta.overallScoreExclusive
  }
  if (delta.geoKind || delta.regionHint || (delta.locationHints && delta.locationHints.length > 0)) {
    out.geoKind = delta.geoKind
    out.regionHint = delta.regionHint ?? null
    out.locationHints = delta.locationHints
  }
  if (delta.procurementType) out.procurementType = delta.procurementType
  return out
}

export function hasFilterDelta(filters: SupplierListFilters): boolean {
  return Boolean(
    filters.riskBand ||
      filters.singleSource ||
      filters.categoryHint ||
      filters.subCategoryHint ||
      filters.nameHint ||
      filters.scoreFilter ||
      filters.overallScoreMin != null ||
      filters.geoKind ||
      filters.regionHint ||
      filters.procurementType ||
      (filters.locationHints && filters.locationHints.length > 0)
  )
}

/**
 * Follow-up that refines the last list/count/group table
 * (e.g. "now EMEA", "only high risk", "from that table, show only for EMEA").
 */
export function isRefineLastTableAsk(text: string): boolean {
  const t = text.toLowerCase().trim()
  if (!t) return false
  if (isHighestRiskQuestion(text) || isWhatsNewAsk(text)) return false

  const hasDelta = hasFilterDelta(extractSupplierListFilters(text))
  const refineCue =
    /^(now|only|just)\b/.test(t) ||
    /\bfilter(ed)?\s+(to|by|for|that|this|the)\b/.test(t) ||
    /\bsame\s+table\b/.test(t) ||
    /\bnarrow\b/.test(t) ||
    /\blimit\s+to\b/.test(t) ||
    /\brestrict\s+to\b/.test(t) ||
    /\bfrom\s+(that|those|the)\s+(results?|list|table)\b/.test(t) ||
    /\b(show|list|give|display)\s+only\s+(for\s+)?\b/.test(t) ||
    /\bonly\s+for\b/.test(t) ||
    /\bjust\s+for\b/.test(t)

  if (refineCue) {
    return hasDelta || /^(now|only|just)\b/.test(t) || /\bfrom\s+(that|those|the)\s+(results?|list|table)\b/.test(t)
  }

  // Short delta-only utterances (no new list/count verb / supplier list ask)
  if (t.length > 90) return false
  if (/\bsuppliers?\b/.test(t)) return false
  if (
    /\b(show|list|give|display|find|how many|tell me|brands?|mitigation|alert|report|summary|commentary|next best|questions)\b/.test(
      t
    )
  ) {
    return false
  }
  return hasDelta
}

/** Rebuild a list session from the last Intelligence View chat table title. */
export function recoverListSessionFromDbTableTitle(title: string): RiskAnalystListSession | null {
  const t = title.trim()
  if (!/^Intelligence View\b/i.test(t)) return null
  const mid = t
    .replace(/^Intelligence View\s*[—–-]\s*/i, '')
    .replace(/\s*\(unique suppliers\)\s*$/i, '')
    .trim()
  if (!mid || /^Suppliers\b/i.test(mid)) return null

  const filters: SupplierListFilters = {}
  for (const bit of mid.split(/\s*·\s*/).map((b) => b.trim()).filter(Boolean)) {
    const risk = bit.match(/^(high|medium|low)\s+risk$/i)
    if (risk) {
      filters.riskBand = risk[1].toLowerCase() as 'high' | 'medium' | 'low'
      continue
    }
    if (/^single[- ]source$/i.test(bit)) {
      filters.singleSource = true
      continue
    }
    if (/^(Direct|Indirect|All)$/i.test(bit)) {
      const p = bit.toLowerCase()
      filters.procurementType =
        p === 'all' ? 'all' : p === 'indirect' ? 'indirect' : 'direct'
      continue
    }
    if (/^(EMEA|APAC|LATAM|NA)$/i.test(bit)) {
      filters.geoKind = 'region'
      filters.regionHint = bit.toUpperCase() === 'NA' ? 'NA' : bit.toUpperCase()
      continue
    }
    if (/^(USA|UK|US)$/i.test(bit) || /^[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?$/.test(bit)) {
      // Country-like token from title (Germany, United States, etc.)
      if (/^(Germany|France|India|China|Brazil|Mexico|Canada|Japan|Thailand|Colombia|South Africa)/i.test(bit) || /United States|United Kingdom/i.test(bit)) {
        filters.geoKind = 'country'
        filters.regionHint = bit
        filters.locationHints = [bit.toLowerCase()]
        continue
      }
    }
    if (/^(packaging|chemicals|fragrance|flavou?rs?)$/i.test(bit) || bit.length > 1) {
      if (!filters.categoryHint) filters.categoryHint = bit
    }
  }
  return { tool: 'list_suppliers', filters }
}

export function extractGroupByDimensions(text: string): GroupByDimension[] {
  const t = text.toLowerCase()
  const dims: GroupByDimension[] = []
  const order: Array<{ dim: GroupByDimension; idx: number }> = []

  const riskIdx = t.search(/\brisk(\s+score|\s+band|\s+level)?\b/)
  const regionIdx = t.search(/\b(region|regions|geo|geography)\b/)
  const categoryIdx = t.search(/\b(categor(y|ies)|industry)\b/)
  const procurementIdx = t.search(/\b(direct|indirect|procurement)\b/)

  if (riskIdx >= 0) order.push({ dim: 'risk', idx: riskIdx })
  if (regionIdx >= 0) order.push({ dim: 'region', idx: regionIdx })
  if (categoryIdx >= 0) order.push({ dim: 'category', idx: categoryIdx })
  if (procurementIdx >= 0) order.push({ dim: 'procurement', idx: procurementIdx })

  order.sort((a, b) => a.idx - b.idx)
  for (const o of order) {
    if (!dims.includes(o.dim)) dims.push(o.dim)
  }

  // Default when cue is present but dims unclear
  if (dims.length === 0) dims.push('region')
  return dims.slice(0, 3)
}
